import { Edit, Visibility } from '@mui/icons-material'
import { Box, IconButton, Typography } from '@mui/material'
import {
    MRT_ColumnDef as MRTColumnDef
} from 'material-react-table'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../app/hooks'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { useGetCustomersQuery, useTrackingNumberTemplateQuery } from '../../../app/redux-fetch/apiQuery'
import CustomAlert from '../../../components/Alerts/CustomAlert'
import AddButton from '../../../components/Buttons/AddButton'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../components/Typographies/TableTitleTypography'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import CreateCustomer from './CreateCustomer'
import EditCustomer from './EditCustomer'
import messages from './messages'


const CustomerTable = () => {
    const { formatMessage } = useIntl()
    const [createOpen, setCreateOpen] = useState(false)

    const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)

    const { tenantId, customerId } = loggedInUser

    const { data: customers, isSuccess: isCustomerLoaded } = useGetCustomersQuery(tenantId ?? -1,
        { refetchOnMountOrArgChange: true })

    const { data: countries, isSuccess: isCountriesLoaded } = useGetCountriesQuery()

    const {
        data: template, isSuccess: isTrackingNumberLoaded
    } = useTrackingNumberTemplateQuery({ tenantId, customerId })

    const isTrackingNumberSetup = useMemo(() => !!template, [template])


    const columns: MRTColumnDef<any>[] = [
        {
            id: 'name',
            enableGlobalFilter: true,
            header: formatMessage(messages.name),
            accessorFn: (row) => row.name ?? '',
            filterFn: (row, id, filterValue) => {
                const value = row.getValue(id) as string
                if (!filterValue) return true
                if (!value || value.trim() === '') return false
                return value.toLowerCase().includes(filterValue.toLowerCase())
            },
        },
        {
            id: 'alias',
            enableGlobalFilter: true,
            header: formatMessage(messages.alias),
            accessorFn: (row) => row.alias ?? '',
            filterFn: (row, id, filterValue) => {
                const value = row.getValue(id) as string
                if (!filterValue) return true
                if (!value || value.trim() === '') return false
                return value.toLowerCase().includes(filterValue.toLowerCase())
            },
        },
        {
            id: 'accountNumber',
            enableGlobalFilter: true,
            header: formatMessage(messages.accountNumber),
            accessorFn: (row) => row.accountNumber ?? '',
            filterFn: (row, id, filterValue) => {
                const value = row.getValue(id) as string
                if (!filterValue) return true
                if (!value || value.trim() === '') return false
                return value.toLowerCase().includes(filterValue.toLowerCase())
            },
        },
        {
            id: 'address1',
            enableGlobalFilter: true,
            header: formatMessage(messages.address1),
            accessorFn: (row) => row.location?.address?.address1 ?? '',
        },
        {
            id: 'city',
            enableGlobalFilter: true,
            header: formatMessage(messages.city),
            accessorFn: (row) => row.location?.address?.city ?? '',
        },
        {
            id: 'state',
            enableGlobalFilter: true,
            header: formatMessage(messages.state),
            accessorFn: (row) => row.location?.address?.state ?? '',
        },
        {
            id: 'postalCode',
            enableGlobalFilter: true,
            header: formatMessage(messages.postalCode),
            accessorFn: (row) => row.location?.address?.postalCode ?? '',
        },
        {
            id: 'country',
            accessorFn: (row) => countries?.find((c: any) => c.id === row.location?.address?.countryId)?.name ?? '',
            enableGlobalFilter: true,
            header: formatMessage(messages.country)
        }
    ]

    const [editOpen, setEditOpen] = useState(false)
    const [viewOpen, setViewOpen] = useState(false)
    const [selectedCustomerId, setSelectedCustomerId] = useState(0)

    return (
        <>
            <SideModalWrapper
                open={createOpen}
                key={'create'}
                headerLabel={formatMessage(messages.createEditCustomer)}
                onClose={() => {
                    setCreateOpen((pv) => {
                        return !pv
                    })
                }}
                withBottomBox={false}
            >
                {<CreateCustomer />}
            </SideModalWrapper>
            <SideModalWrapper
                open={editOpen}
                key={'edit'}
                headerLabel={formatMessage(messages.createEditCustomer)}
                onClose={() => {
                    setEditOpen((pv) => {
                        return !pv
                    })
                }}
                withBottomBox={false}
            >
                <ConditionalRender condition={editOpen}>
                    <EditCustomer customerId={selectedCustomerId} />
                </ConditionalRender>
            </SideModalWrapper>
            <SideModalWrapper
                open={viewOpen}
                key={'view'}
                headerLabel={formatMessage(messages.viewCustomer)}
                onClose={() => {
                    setViewOpen((pv) => {
                        return !pv
                    })
                }}
                withBottomBox={false}
            >
                <ConditionalRender condition={viewOpen}>
                    <EditCustomer customerId={selectedCustomerId} isDisabled={true} />
                </ConditionalRender>
            </SideModalWrapper>
            {!isTrackingNumberSetup && isCustomerLoaded && isTrackingNumberLoaded && (<CustomAlert
                severity='error'
                title='Error'
                message='You must setup your tracking number template before you can add a new customer.'
            />)}
            <MRTDataGridv2
                initialState={{
                    pagination: { pageSize: 100, pageIndex: 0 },
                    columnPinning: { left: ['name'] },
                    sorting: [
                        {
                            id: 'name',
                            desc: false,
                        }]
                }
                }
                state={{
                    isLoading: !isCustomerLoaded || !isCountriesLoaded,
                }}
                leftHeadingComponent={
                    <Box sx={{ display: 'inline-flex' }}>
                        <TableTitleTypography>{formatMessage(messages.customers)}</TableTitleTypography>
                    </Box>
                }
                rightHeadingComponent={
                    <AddButton
                        text={formatMessage(messages.newCustomerLbl)}
                        disabled={!isTrackingNumberSetup}
                        onClick={() => setCreateOpen(true)}
                    />
                }
                columns={columns}
                data={customers || []}
                getRowId={(row) => row.id as any}
                enableEditing
                enableColumnPinning={true}
                enableRowActions
                positionActionsColumn='last'
                renderRowActions={({ row, table }: any) => {
                    return (
                        <Box sx={{ display: 'flex' }}>
                            <IconButton
                                color={'info'}
                                onClick={() => {
                                    setSelectedCustomerId(row.id)
                                    setViewOpen(true)
                                }}
                            >
                                <Visibility />
                            </IconButton>
                            <IconButton
                                color={'info'}
                                onClick={() => {
                                    setSelectedCustomerId(row.id)
                                    setEditOpen(true)
                                }}
                            >
                                <Edit />
                            </IconButton>
                        </Box>
                    )
                }}
            />
        </>)
}

export default CustomerTable
